/**
 * Centralised AI error taxonomy.
 *
 * Providers MUST translate their native failures into one of these classes.
 * No provider-specific error object, HTTP body, or SDK exception may reach
 * the UI layer.
 */

import type { AIProviderName } from './types';

export type AIErrorCode =
  | 'rate_limit'
  | 'provider_unavailable'
  | 'invalid_request'
  | 'quota_exceeded'
  | 'configuration'
  | 'timeout'
  | 'content_filtered'
  | 'unknown';

export interface AIErrorOptions {
  provider?: AIProviderName;
  model?: string;
  /** Upstream status code, when the failure came from an HTTP call. */
  status?: number;
  /** Original error, kept for logging only — never rendered. */
  cause?: unknown;
  /** Whether a retry (with backoff) could plausibly succeed. */
  retryable?: boolean;
  /** Suggested wait before retrying, in milliseconds. */
  retryAfterMs?: number;
  /**
   * Machine-readable sub-reason from the transport, e.g. 'quota_exceeded'
   * vs 'tier_blocked'. Provider-agnostic: it must be a platform vocabulary
   * string, never a vendor error code.
   */
  reason?: string;
}

/** Base class for every error surfaced by the AI platform. */
export class AIError extends Error {
  readonly code: AIErrorCode;
  readonly provider?: AIProviderName;
  readonly model?: string;
  readonly status?: number;
  readonly retryable: boolean;
  readonly retryAfterMs?: number;
  readonly reason?: string;

  constructor(code: AIErrorCode, message: string, options: AIErrorOptions = {}) {
    super(message);
    if (options.cause !== undefined) {
      (this as { cause?: unknown }).cause = options.cause;
    }
    this.name = new.target.name;
    this.code = code;
    this.provider = options.provider;
    this.model = options.model;
    this.status = options.status;
    this.retryable = options.retryable ?? false;
    this.retryAfterMs = options.retryAfterMs;
    this.reason = options.reason;
  }

  /** Safe, provider-agnostic shape for logging or transport. */
  toJSON() {
    return {
      code: this.code,
      message: this.message,
      provider: this.provider,
      model: this.model,
      retryable: this.retryable,
      reason: this.reason,
    };
  }
}

/** Provider refused the request because the caller is sending too fast. */
export class RateLimitError extends AIError {
  constructor(message = 'AI provider rate limit reached', options: AIErrorOptions = {}) {
    super('rate_limit', message, { retryable: true, ...options });
  }
}

/** Provider is down, unreachable, or returned a 5xx. */
export class ProviderUnavailableError extends AIError {
  constructor(message = 'AI provider is temporarily unavailable', options: AIErrorOptions = {}) {
    super('provider_unavailable', message, { retryable: true, ...options });
  }
}

/** The request itself is malformed or unsupported by the provider. Not retryable. */
export class InvalidRequestError extends AIError {
  constructor(message = 'Invalid AI request', options: AIErrorOptions = {}) {
    super('invalid_request', message, { retryable: false, ...options });
  }
}

/** Billing/credit or subscription quota exhausted. Not retryable. */
export class QuotaExceededError extends AIError {
  constructor(message = 'AI quota exceeded', options: AIErrorOptions = {}) {
    super('quota_exceeded', message, { retryable: false, ...options });
  }
}

/** Missing key, unknown provider, or otherwise unusable configuration. */
export class AIConfigurationError extends AIError {
  constructor(message = 'AI platform is not configured correctly', options: AIErrorOptions = {}) {
    super('configuration', message, { retryable: false, ...options });
  }
}

/** The call exceeded the configured timeout. */
export class AITimeoutError extends AIError {
  constructor(message = 'AI request timed out', options: AIErrorOptions = {}) {
    super('timeout', message, { retryable: true, ...options });
  }
}

/** Provider safety systems blocked the prompt or the completion. */
export class ContentFilteredError extends AIError {
  constructor(message = 'AI request was blocked by content safety filters', options: AIErrorOptions = {}) {
    super('content_filtered', message, { retryable: false, ...options });
  }
}

export const isAIError = (value: unknown): value is AIError => value instanceof AIError;

/**
 * Map an HTTP status from any provider to the canonical error class.
 * Providers should use this instead of inventing their own mapping.
 */
export function aiErrorFromStatus(
  status: number,
  message: string,
  options: AIErrorOptions = {},
): AIError {
  const opts = { ...options, status };
  if (status === 429) return new RateLimitError(message, opts);
  if (status === 402) return new QuotaExceededError(message, opts);
  if (status === 401 || status === 403) return new AIConfigurationError(message, opts);
  if (status === 408 || status === 504) return new AITimeoutError(message, opts);
  if (status >= 500) return new ProviderUnavailableError(message, opts);
  if (status >= 400) return new InvalidRequestError(message, opts);
  return new AIError('unknown', message, opts);
}

/** Normalise anything thrown inside the AI stack into an `AIError`. */
export function toAIError(error: unknown, options: AIErrorOptions = {}): AIError {
  if (isAIError(error)) return error;
  if (error instanceof DOMException && error.name === 'AbortError') {
    return new AITimeoutError('AI request was aborted', { ...options, cause: error });
  }
  const message = error instanceof Error ? error.message : 'Unexpected AI failure';
  return new AIError('unknown', message, { ...options, cause: error });
}
